import { Fragment, useState } from "react";
import { AccordionControl } from "shared/app/ExplainRecommendationModal/components/AccordionControl";
import { fieldTypeOperatorOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/constants";
import {
  FieldType,
  Rule
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Application } from "types/application";
import { getFullName } from "shared/utils/get-full-name";
import { Reasons } from "types/application";
import { formatTitleFirstWord } from "utils/formatTitle";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import { cx } from "shared/utils";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { VerticalSpacer } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/components/VerticalSpacer";
import {
  formatConditionTitle,
  formatThreshold
} from "shared/app/ExplainRecommendationModal/utils";

interface AccordionProps {
  rule: Rule;
  ruleSet: Reasons;
  application: Application;
  idx?: number;
}

export const Accordion = ({
  rule,
  ruleSet,
  application,
  idx = 0
}: AccordionProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <Fragment>
      <Show if={idx !== 0}>
        <div>
          <VerticalSpacer height={16} className="ml-6" />
          <Badge variant={BadgeVariant.BASE} size={BadgeSize.BASE}>
            AND
          </Badge>
          <VerticalSpacer height={16} className="ml-6" />
        </div>
      </Show>

      <div className="flex w-full flex-col">
        <div
          className={cx(
            "flex w-full border border-alpha-100 items-center",
            isAccordionOpen[ruleSet.rule_set_id] ? "rounded-t-lg" : "rounded-lg"
          )}
        >
          <div
            className={cx(
              "max-w-full w-full bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100",
              isAccordionOpen[ruleSet.rule_set_id]
                ? "rounded-tl-lg"
                : "rounded-l-lg"
            )}
          >
            <p className="text-content-1000 font-medium text-sm truncate">
              {formatConditionTitle(rule.field_name)}
            </p>
          </div>
          <Show if={rule.field_type}>
            <div className="w-full max-w-[135px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100">
              <p className="text-content-1000 font-medium text-sm">
                {formatTitleFirstWord(rule.field_type || FieldType.Any)}
              </p>
            </div>
          </Show>
          {/* After the migration all templates should have a group_type, so Show condition can be removed after */}
          <Show if={rule.group_type}>
            <div className="w-full max-w-[145px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100">
              <p className="text-content-1000 font-medium text-sm">
                {formatTitleFirstWord(rule.group_type || "No Grouping")}
              </p>
            </div>
          </Show>
          <div className="w-full max-w-[48px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100">
            <p className="text-content-1000 font-medium text-sm">
              {
                fieldTypeOperatorOptions.find(
                  (el) => el.value === rule.operator
                )?.label
              }
            </p>
          </div>
          <div className="w-fit bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100">
            <p className="text-content-1000 font-medium text-sm">
              {formatThreshold(rule)}
            </p>
          </div>
          <AccordionControl
            itemResult={rule.result}
            itemId={ruleSet.rule_set_id}
            isAccordionOpen={isAccordionOpen}
            setIsAccordionOpen={setIsAccordionOpen}
          />
        </div>

        {isAccordionOpen[ruleSet.rule_set_id] && (
          <div className="bg-background-1000 p-6 space-y-4 rounded-b-lg border-x border-b border-alpha-100">
            {rule.per_applicant_results ? (
              rule.per_applicant_results.map((applicant) => {
                const applicantData = application.applicants.find(
                  (el) => el.id === applicant.applicant_id
                );

                if (!applicantData) return null;

                return (
                  <div
                    key={applicant.applicant_id}
                    className="flex w-full justify-between gap-4 text-xs text-content-1000 font-medium"
                  >
                    <p>
                      {getFullName({
                        firstName: applicantData.first_name,
                        lastName: applicantData.last_name,
                        middleName: applicantData.middle_name,
                        short: true
                      })}
                    </p>

                    <div className="flex gap-3 items-center">
                      <p>{applicant.value}</p>
                      {applicant.result ? (
                        <SuccessIcon className="w-4 shrink-0 text-primary-500" />
                      ) : (
                        <FailedIcon className="w-4 shrink-0 text-content-100" />
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex w-full justify-between gap-4 text-xs text-content-1000 font-medium">
                <p>
                  Group {rule.field_type}{" "}
                  {formatTitleFirstWord(rule.field_name).toLocaleLowerCase()}
                </p>

                <div className="flex gap-3 items-center">
                  <p>{rule.computed_value}</p>
                  {rule.result ? (
                    <SuccessIcon className="w-4 shrink-0 text-primary-500" />
                  ) : (
                    <FailedIcon className="w-4 shrink-0 text-content-100" />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
};
